import React, { CSSProperties, MouseEvent, useEffect, useState } from 'react';

// Shared modal header (RNA-4918).
export type ModalHeaderData = {
  title?: string;
  showShare?: boolean;
  showClose?: boolean;
  shared?: boolean;
  sharedTooltipText?: string;
  onShare?: () => void;
  onClose?: () => void;
  dismissModal?: boolean;
  closeButtonId?: string;
  closeAriaLabel?: string;
  shareButtonId?: string;
  shareAriaLabel?: string;
  dataTestId?: string;
  imageUrl?: string;
};

type ModalHeaderProps = {
  data: ModalHeaderData;
};

function ShareIcon() {
  return (
    <svg
      className="modal-header-bar__share-icon"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      className="modal-header-bar__close-icon"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
    </svg>
  );
}

export function ModalHeader({ data }: ModalHeaderProps) {
  if (!data) {
    return null;
  }

  const {
    title,
    showShare = false,
    showClose = true,
    shared = false,
    sharedTooltipText,
    onShare,
    onClose,
    dismissModal = true,
    closeButtonId,
    closeAriaLabel = 'Close',
    shareButtonId,
    shareAriaLabel = 'Share',
    dataTestId,
    imageUrl,
  } = data;

  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    if (!imageUrl) {
      setImageFailed(false);
      return;
    }
    setImageFailed(false);
    const probe = new window.Image();
    let cancelled = false;
    probe.onload = () => {
      if (!cancelled && probe.naturalWidth === 0) {
        setImageFailed(true);
      }
    };
    probe.onerror = () => {
      if (!cancelled) {
        setImageFailed(true);
      }
    };
    probe.src = imageUrl;
    return () => {
      cancelled = true;
      probe.onload = null;
      probe.onerror = null;
    };
  }, [imageUrl]);

  const showImage = !!imageUrl && !imageFailed;
  const rootClassName = showImage
    ? 'modal-header-bar modal-header-bar--with-image'
    : 'modal-header-bar';
  const rootStyle: CSSProperties | undefined = showImage
    ? { backgroundImage: 'url(' + imageUrl + ')' }
    : undefined;

  const handleShare = (event: MouseEvent<HTMLButtonElement>) => {
    if (!onShare) {
      return;
    }
    event.preventDefault();
    onShare();
  };

  const handleClose = (event: MouseEvent<HTMLButtonElement>) => {
    if (onClose) {
      onClose();
    }
    if (!dismissModal) {
      event.preventDefault();
    }
  };

  return (
    <div
      className={rootClassName}
      style={rootStyle}
      data-testid={dataTestId || undefined}
      role="banner"
    >
      {showShare ? (
        <button
          type="button"
          id={shareButtonId || undefined}
          className="modal-header-bar__share"
          onClick={handleShare}
          aria-label={shareAriaLabel}
          data-testid="modal-header-share"
        >
          <ShareIcon />
          <span
            className={
              'modal-header-bar__tooltip' + (shared ? ' modal-header-bar__tooltip--expanded' : '')
            }
            role="status"
            aria-live="polite"
          >
            <span className="modal-header-bar__tooltip-text">{sharedTooltipText}</span>
          </span>
        </button>
      ) : (
        <span className="modal-header-bar__spacer" aria-hidden="true" />
      )}

      {title ? <span className="modal-header-bar__title">{title}</span> : null}

      {showClose ? (
        <button
          type="button"
          id={closeButtonId || undefined}
          className="modal-header-bar__close"
          onClick={handleClose}
          aria-label={closeAriaLabel}
          data-dismiss={dismissModal ? 'modal' : undefined}
          data-testid="modal-header-close"
        >
          <CloseIcon />
        </button>
      ) : (
        <span className="modal-header-bar__spacer" aria-hidden="true" />
      )}
    </div>
  );
}
